import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { useAuth } from '../../hooks/useAuth'
import { useSetting } from '../../hooks/useSettings'
import { groupService } from '../../services/group.service'
import { organizationService } from '../../services/organization.service'
import { PermissionAction, PermissionType, permissionsService } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { userService } from '../../services/user.service'
import { Group, Organization, User } from '../../types'
import { parseBackendErrors } from '../../utils/errorHandler'
import GroupEditModal from '../Groups/GroupEditModal'
import OrganizationEditModal from '../Organizations/OrganizationEditModal'
import UserEditModal from '../Users/UserEditModal'
import ConfirmDialog from '../common/ConfirmDialog'
import IfPermissions from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import { Pagination } from '../common/Pagination'
import './UsersAndGroups.scss'

enum UsersGroupsTab {
  Organizations = 'organizations',
  Users = 'users',
  Groups = 'groups',
}

export const UsersAndGroups: React.FC = () => {
  const { t } = useTranslation()
  const { user: currentUser } = useAuth()
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)

  const canDisplayOrganizations = permissionsService.currentUserCan(PermissionAction.Display, PermissionType.Organization)
  const canDisplayUsers = permissionsService.currentUserCan(PermissionAction.Display, PermissionType.User)
  const canDisplayGroups = permissionsService.currentUserCan(PermissionAction.Display, PermissionType.Group)

  const firstVisibleTab: UsersGroupsTab = canDisplayOrganizations
    ? UsersGroupsTab.Organizations
    : canDisplayUsers
      ? UsersGroupsTab.Users
      : canDisplayGroups
        ? UsersGroupsTab.Groups
        : UsersGroupsTab.Organizations

  const [activeTab, setActiveTab] = useState<UsersGroupsTab>(firstVisibleTab)

  // Keep the active tab in sync if visibility flips (e.g. after org switch).
  useEffect(() => {
    const stillVisible =
      (activeTab === UsersGroupsTab.Organizations && canDisplayOrganizations) ||
      (activeTab === UsersGroupsTab.Users && canDisplayUsers) ||
      (activeTab === UsersGroupsTab.Groups && canDisplayGroups)
    if (!stillVisible) {
      setActiveTab(firstVisibleTab)
    }
  }, [activeTab, canDisplayOrganizations, canDisplayUsers, canDisplayGroups, firstVisibleTab])
  const [users, setUsers] = useState<User[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [usersCount, setUsersCount] = useState(0)
  const [groupsCount, setGroupsCount] = useState(0)
  const [organizationsCount, setOrganizationsCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchSupported, setSearchSupported] = useState({ users: true, groups: true })
  const [initialLoad, setInitialLoad] = useState(true)

  const [usersPage, setUsersPage] = useState(1)
  const [groupsPage, setGroupsPage] = useState(1)
  const [usersSortField, setUsersSortField] = useState<string>('')
  const [groupsSortField, setGroupsSortField] = useState<string>('')
  const [usersSortReverse, setUsersSortReverse] = useState(false)
  const [groupsSortReverse, setGroupsSortReverse] = useState(false)
  const [usersFilter, setUsersFilter] = useState('')
  const [groupsFilter, setGroupsFilter] = useState('')
  const [usersFilterInput, setUsersFilterInput] = useState('')
  const [groupsFilterInput, setGroupsFilterInput] = useState('')
  const usersFilterInputRef = useRef<HTMLInputElement | null>(null)
  const groupsFilterInputRef = useRef<HTMLInputElement | null>(null)
  const maintainUsersFilterFocusRef = useRef(false)
  const maintainGroupsFilterFocusRef = useRef(false)

  const [showUserModal, setShowUserModal] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [showOrganizationModal, setShowOrganizationModal] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [confirmTitle, setConfirmTitle] = useState('')
  const [confirmMessage, setConfirmMessage] = useState('')

  useEffect(() => {
    document.title = `${t('users_and_groups')} - ${appTitle}`
  }, [t, appTitle])

  const loadUsers = useCallback(async () => {
    try {
      const response = await userService.listFiltered(usersPage, undefined, usersSortField, usersSortReverse, usersFilter, false, {
        page_size: DEFAULT_PAGE_SIZE,
        full_perms: true,
      })
      setUsers(response.results)
      setUsersCount(response.count)
      setSearchSupported((prev) => ({ ...prev, users: true }))
    } catch (err: any) {
      if (err?.response?.status === 400 || err?.response?.status === 500) {
        setSearchSupported((prev) => ({ ...prev, users: false }))
      }
      toastService.showError(t('error_loading_users'))
    }
  }, [usersPage, usersSortField, usersSortReverse, usersFilter, t])

  const loadGroups = useCallback(async () => {
    try {
      const response = await groupService.listFiltered(groupsPage, undefined, groupsSortField, groupsSortReverse, groupsFilter, false, {
        page_size: DEFAULT_PAGE_SIZE,
        full_perms: true,
      })
      setGroups(response.results)
      setGroupsCount(response.count)
      setSearchSupported((prev) => ({ ...prev, groups: true }))
    } catch (err: any) {
      if (err?.response?.status === 400 || err?.response?.status === 500) {
        setSearchSupported((prev) => ({ ...prev, groups: false }))
      }
      toastService.showError(t('error_loading_groups'))
    }
  }, [groupsPage, groupsSortField, groupsSortReverse, groupsFilter, t])

  const loadOrganizations = useCallback(async () => {
    try {
      const response = await organizationService.listAll({ page_size: DEFAULT_PAGE_SIZE })
      setOrganizations(response.results)
      setOrganizationsCount(response.count)
    } catch (err: any) {
      toastService.showError(t('error_loading_organizations'))
    }
  }, [t])

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)
        if (activeTab === UsersGroupsTab.Users) {
          await loadUsers()
        } else if (activeTab === UsersGroupsTab.Organizations) {
          await loadOrganizations()
        } else {
          await loadGroups()
        }
        setInitialLoad(false)
      } catch (err) {
        setError(t('error_loading_users_and_groups'))
        toastService.showError(t('error_loading_users_and_groups'))
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [
    activeTab,
    usersPage,
    usersSortField,
    usersSortReverse,
    usersFilter,
    groupsPage,
    groupsSortField,
    groupsSortReverse,
    groupsFilter,
    loadUsers,
    loadOrganizations,
    loadGroups,
    t,
  ])

  useEffect(() => {
    if (usersFilterInput === usersFilter) {
      return
    }
    const handler = setTimeout(() => {
      setUsersFilter(usersFilterInput)
      setUsersPage(1)
    }, 400)
    return () => clearTimeout(handler)
  }, [usersFilterInput, usersFilter])

  useEffect(() => {
    if (groupsFilterInput === groupsFilter) {
      return
    }
    const handler = setTimeout(() => {
      setGroupsFilter(groupsFilterInput)
      setGroupsPage(1)
    }, 400)
    return () => clearTimeout(handler)
  }, [groupsFilterInput, groupsFilter])

  useEffect(() => {
    if (!loading && !initialLoad && maintainUsersFilterFocusRef.current && usersFilterInputRef.current) {
      const input = usersFilterInputRef.current
      input.focus()
      const length = input.value.length
      input.setSelectionRange(length, length)
    }
  }, [loading, initialLoad])

  useEffect(() => {
    if (!loading && !initialLoad && maintainGroupsFilterFocusRef.current && groupsFilterInputRef.current) {
      const input = groupsFilterInputRef.current
      input.focus()
      const length = input.value.length
      input.setSelectionRange(length, length)
    }
  }, [loading, initialLoad])

  const handleUsersSort = useCallback(
    (column: string) => {
      setUsersSortField((prevField) => {
        if (prevField !== column) {
          setUsersSortReverse(false)
          return column
        } else if (prevField === column && !usersSortReverse) {
          setUsersSortReverse(true)
          return prevField
        } else {
          setUsersSortReverse(false)
          return ''
        }
      })
    },
    [usersSortReverse]
  )

  const handleGroupsSort = useCallback(
    (column: string) => {
      setGroupsSortField((prevField) => {
        if (prevField !== column) {
          setGroupsSortReverse(false)
          return column
        } else if (prevField === column && !groupsSortReverse) {
          setGroupsSortReverse(true)
          return prevField
        } else {
          setGroupsSortReverse(false)
          return ''
        }
      })
    },
    [groupsSortReverse]
  )

  const handleEditUser = (user?: User) => {
    setSelectedUser(user || null)
    setShowUserModal(true)
  }

  const handleDeleteUser = (user: User) => {
    if (user.is_superuser) {
      toastService.showError(t('cannot_delete_superuser_user', { username: user.username }))
      return
    }
    setSelectedUser(user)
    setConfirmTitle(t('confirm_delete_user_account'))
    setConfirmMessage(t('this_operation_will_permanently_delete_this_user_account'))
    setConfirmAction(() => () => deleteUser(user))
    setShowConfirmDialog(true)
  }

  const handleEditGroup = (group?: Group) => {
    setSelectedGroup(group || null)
    setShowGroupModal(true)
  }

  const handleDeleteGroup = (group: Group) => {
    setSelectedGroup(group)
    setConfirmTitle(t('confirm_delete_user_group'))
    setConfirmMessage(t('this_operation_will_permanently_delete_this_user_group'))
    setConfirmAction(() => () => deleteGroup(group))
    setShowConfirmDialog(true)
  }

  const deleteUser = async (user: User) => {
    try {
      await userService.delete(user.id)
      toastService.showSuccess(t('deleted_user', { username: user.username }))
      await loadUsers()
    } catch (error: any) {
      const { generalMessage } = parseBackendErrors(error, t, t('error_deleting_user', { username: user.username }))
      toastService.showError(generalMessage)
    }
  }

  const deleteGroup = async (group: Group) => {
    try {
      await groupService.delete(group.id)
      toastService.showSuccess(t('deleted_group', { name: group.name }))
      await loadGroups()
    } catch (error) {
      toastService.showError(t('error_deleting_group', { name: group.name }))
    }
  }

  const handleUserSuccess = async (user: User, meta?: { passwordChanged?: boolean }) => {
    if (user.id === currentUser?.id && meta?.passwordChanged) {
      toastService.showInfo(t('password_has_been_changed_you_will_be_logged_out_momentarily'))
      setTimeout(() => {
        window.location.href = '/accounts/logout/?next=/accounts/login/?next=/'
      }, 2500)
    } else {
      await loadUsers()
    }
  }

  const handleGroupSuccess = async () => {
    await loadGroups()
  }

  const handleUserError = () => {
    toastService.showError(t('error_saving_user'))
  }

  const handleGroupError = () => {
    toastService.showError(t('error_saving_group'))
  }

  const handleOrganizationSuccess = async (organization: Organization) => {
    if (selectedOrganization) {
      toastService.showSuccess(t('saved_organization', { name: organization.name }))
    } else {
      toastService.showSuccess(t('created_organization', { name: organization.name }))
    }
    await loadOrganizations()
  }

  const handleOrganizationError = () => {
    toastService.showError(t('error_saving_organization'))
  }

  const handleCreateOrganization = () => {
    setSelectedOrganization(null)
    setShowOrganizationModal(true)
  }

  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)
    setShowOrganizationModal(true)
  }

  const handleDeleteOrganization = (organization: Organization) => {
    setConfirmTitle(t('confirm_delete_organization'))
    setConfirmMessage(t('this_operation_will_permanently_delete_this_organization'))
    const deletedCurrentOrganization = organization.id === settingsService.getCurrentOrganizationId()
    setConfirmAction(() => async () => {
      try {
        await organizationService.delete(organization.id)
        toastService.showSuccess(t('deleted_organization', { name: organization.name }))
        if (deletedCurrentOrganization) {
          window.location.reload()
          return
        }
        await loadOrganizations()
      } catch (error) {
        const { generalMessage } = parseBackendErrors(error, t, t('error_deleting_organization', { name: organization.name }))
        toastService.showError(generalMessage)
      }
    })
    setShowConfirmDialog(true)
  }

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction()
      setShowConfirmDialog(false)
      setConfirmAction(null)
    }
  }

  const getGroupName = (groupId: number): string => {
    const group = groups.find((g) => g.id === groupId)
    return group ? group.name : `Group ${groupId}`
  }

  const usersTotalPages = Math.max(1, Math.ceil(usersCount / DEFAULT_PAGE_SIZE))
  const groupsTotalPages = Math.max(1, Math.ceil(groupsCount / DEFAULT_PAGE_SIZE))
  const usersRangeStart = usersCount === 0 ? 0 : (usersPage - 1) * DEFAULT_PAGE_SIZE + 1
  const usersRangeEnd = Math.min(usersCount, usersPage * DEFAULT_PAGE_SIZE)
  const groupsRangeStart = groupsCount === 0 ? 0 : (groupsPage - 1) * DEFAULT_PAGE_SIZE + 1
  const groupsRangeEnd = Math.min(groupsCount, groupsPage * DEFAULT_PAGE_SIZE)

  if (loading) {
    return (
      <div className="users-groups">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
          <p className="mt-2">{t('loading_users_and_groups')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="users-groups">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="users-groups">
      <PageHeader title={t('users_and_groups')} info={t('users_and_groups_description') || t('users_groups_info')} infoLink="users_groups" />

      <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k as UsersGroupsTab)} className="mb-3">
        {canDisplayOrganizations && (
        <Tab eventKey={UsersGroupsTab.Organizations} title={t('organizations')}>
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{t('organizations')}</h4>
              <IfPermissions action={PermissionAction.Add} type={PermissionType.Organization}>
                <button className="btn btn-primary btn-sm" onClick={handleCreateOrganization}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_organization')}
                </button>
              </IfPermissions>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('users')}</th>
                    <th>{t('active')}</th>
                    <th className="text-end">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('no_items_found')} {t('organizations')}
                      </td>
                    </tr>
                  ) : (
                    organizations.map((organization) => (
                      <tr key={organization.id}>
                        <td>{organization.name}</td>
                        <td>{organization.users_count ?? 0}</td>
                        <td>
                          {organization.is_active ? (
                            <span className="badge bg-success">{t('active')}</span>
                          ) : (
                            <span className="badge bg-secondary">{t('disabled')}</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <IfPermissions action={PermissionAction.Change} type={PermissionType.Organization}>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditOrganization(organization)} title={t('edit')}>
                                <i className="bi bi-pencil"></i>
                              </button>
                            </IfPermissions>
                            <IfPermissions action={PermissionAction.Delete} type={PermissionType.Organization}>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteOrganization(organization)} title={t('delete')}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </IfPermissions>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="text-muted small">{organizationsCount}</div>
          </div>
        </Tab>
        )}

        {canDisplayUsers && (
        <Tab eventKey={UsersGroupsTab.Users} title={t('users')}>
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{t('users')}</h4>
              <IfPermissions action={PermissionAction.Add} type={PermissionType.User}>
                <button className="btn btn-primary btn-sm" onClick={() => handleEditUser()}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_user')}
                </button>
              </IfPermissions>
            </div>

            {searchSupported.users && (
              <div className="row mb-3">
                <div className="col-md mb-2 mb-xl-0">
                  <div className="form-inline d-flex align-items-center">
                    <label className="text-muted me-2 mb-0">{t('filter_by')}:</label>
                    <input
                      className="form-control form-control-sm flex-fill w-auto"
                      type="text"
                      ref={usersFilterInputRef}
                      value={usersFilterInput}
                      onChange={(e) => setUsersFilterInput(e.target.value)}
                      onFocus={() => {
                        maintainUsersFilterFocusRef.current = true
                      }}
                      onBlur={() => {
                        maintainUsersFilterFocusRef.current = false
                      }}
                      placeholder={t('username')}
                    />
                  </div>
                </div>
                {usersTotalPages > 1 && (
                  <div className="col-auto d-none d-md-block">
                    <Pagination currentPage={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} alwaysVisible />
                  </div>
                )}
              </div>
            )}

            {!searchSupported.users && usersTotalPages > 1 && (
              <div className="row mb-3">
                <div className="col-auto ms-auto">
                  <Pagination currentPage={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} alwaysVisible />
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleUsersSort('username')}>
                      {t('username')}
                      {usersSortField === 'username' && <i className={`bi bi-arrow-${usersSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th className="sortable" onClick={() => handleUsersSort('first_name')}>
                      {t('name')}
                      {usersSortField === 'first_name' && <i className={`bi bi-arrow-${usersSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th>{t('groups')}</th>
                    <th>{t('active')}</th>
                    <th>{t('ad.user_source_column')}</th>
                    <th className="text-end">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('no_items_found')} {t('users')}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <button
                            className="btn btn-link p-0 text-start"
                            onClick={() => handleEditUser(user)}
                            disabled={!permissionsService.currentUserCan(PermissionAction.Change, PermissionType.User)}
                          >
                            {user.username}
                          </button>
                        </td>
                        <td>
                          {user.first_name} {user.last_name}
                        </td>
                        <td>
                          {user.groups_detail && user.groups_detail.length > 0
                            ? user.groups_detail.map((g) => g.name).join(', ')
                            : user.groups && user.groups.length > 0
                              ? user.groups.map(getGroupName).join(', ')
                              : '-'}
                        </td>
                        <td>
                          {user.is_active ? (
                            <span className="badge bg-success">{t('active')}</span>
                          ) : (
                            <span className="badge bg-secondary">{t('disabled')}</span>
                          )}
                        </td>
                        <td>
                          {user.auth_source === 'ldap' ? (
                            <span className="badge bg-info" title={t('ad.user_source_ldap_help')}>
                              <i className="bi bi-diagram-3 me-1"></i>
                              {t('ad.user_source_ldap')}
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark border">{t('ad.user_source_local')}</span>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <IfPermissions action={PermissionAction.Change} type={PermissionType.User}>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditUser(user)} title={t('edit')}>
                                <i className="bi bi-pencil"></i>
                              </button>
                            </IfPermissions>
                            <IfPermissions action={PermissionAction.Delete} type={PermissionType.User}>
                              <span title={user.is_superuser ? t('superuser') : t('delete')}>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteUser(user)} disabled={user.is_superuser}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </span>
                            </IfPermissions>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">{usersCount > 0 ? `${usersRangeStart} - ${usersRangeEnd} / ${usersCount}` : '0 / 0'}</div>
              <Pagination currentPage={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} alwaysVisible />
            </div>
          </div>
        </Tab>
        )}

        {canDisplayGroups && (
        <Tab eventKey={UsersGroupsTab.Groups} title={t('groups')}>
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{t('groups')}</h4>
              <IfPermissions action={PermissionAction.Add} type={PermissionType.Group}>
                <button className="btn btn-primary btn-sm" onClick={() => handleEditGroup()}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_group')}
                </button>
              </IfPermissions>
            </div>

            {searchSupported.groups && (
              <div className="row mb-3">
                <div className="col-md mb-2 mb-xl-0">
                  <div className="form-inline d-flex align-items-center">
                    <label className="text-muted me-2 mb-0">{t('filter_by')}:</label>
                    <input
                      className="form-control form-control-sm flex-fill w-auto"
                      type="text"
                      ref={groupsFilterInputRef}
                      value={groupsFilterInput}
                      onChange={(e) => setGroupsFilterInput(e.target.value)}
                      onFocus={() => {
                        maintainGroupsFilterFocusRef.current = true
                      }}
                      onBlur={() => {
                        maintainGroupsFilterFocusRef.current = false
                      }}
                      placeholder={t('name')}
                    />
                  </div>
                </div>
                {groupsTotalPages > 1 && (
                  <div className="col-auto d-none d-md-block">
                    <Pagination currentPage={groupsPage} totalPages={groupsTotalPages} onPageChange={setGroupsPage} alwaysVisible />
                  </div>
                )}
              </div>
            )}

            {!searchSupported.groups && groupsTotalPages > 1 && (
              <div className="row mb-3">
                <div className="col-auto ms-auto">
                  <Pagination currentPage={groupsPage} totalPages={groupsTotalPages} onPageChange={setGroupsPage} alwaysVisible />
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleGroupsSort('name')}>
                      {t('name')}
                      {groupsSortField === 'name' && <i className={`bi bi-arrow-${groupsSortReverse ? 'up' : 'down'} ms-1`}></i>}
                    </th>
                    <th className="text-end">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-muted py-4">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('no_items_found')} {t('groups')}
                      </td>
                    </tr>
                  ) : (
                    groups.map((group) => (
                      <tr key={group.id}>
                        <td>
                          <button
                            className="btn btn-link p-0 text-start"
                            onClick={() => handleEditGroup(group)}
                            disabled={!permissionsService.currentUserCan(PermissionAction.Change, PermissionType.Group)}
                          >
                            {group.name}
                          </button>
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            <IfPermissions action={PermissionAction.Change} type={PermissionType.Group}>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEditGroup(group)} title={t('edit')}>
                                <i className="bi bi-pencil"></i>
                              </button>
                            </IfPermissions>
                            <IfPermissions action={PermissionAction.Delete} type={PermissionType.Group}>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteGroup(group)} title={t('delete')}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </IfPermissions>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">{groupsCount > 0 ? `${groupsRangeStart} - ${groupsRangeEnd} / ${groupsCount}` : '0 / 0'}</div>
              <Pagination currentPage={groupsPage} totalPages={groupsTotalPages} onPageChange={setGroupsPage} alwaysVisible />
            </div>
          </div>
        </Tab>
        )}
      </Tabs>

      <UserEditModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        user={selectedUser}
        onSuccess={handleUserSuccess}
        onError={handleUserError}
      />

      <GroupEditModal
        show={showGroupModal}
        onHide={() => setShowGroupModal(false)}
        group={selectedGroup}
        onSuccess={handleGroupSuccess}
        onError={handleGroupError}
      />

      <OrganizationEditModal
        show={showOrganizationModal}
        onHide={() => setShowOrganizationModal(false)}
        organization={selectedOrganization}
        onSuccess={handleOrganizationSuccess}
        onError={handleOrganizationError}
      />

      <ConfirmDialog
        show={showConfirmDialog}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={t('proceed')}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  )
}
